import { create } from 'zustand';
// @ts-nocheck
import api from '../utils/api';

export interface EmailSignature {
  id: string;
  name: string;
  content: string;
  isDefault: boolean;
}

export interface EmailFilter {
  id: string;
  name: string;
  conditions: FilterCondition[];
  actions: FilterAction[];
  enabled: boolean;
}

export interface FilterCondition {
  field: string;
  operator: string;
  value: string;
}

export interface FilterAction {
  type: string;
  value?: string;
}

export interface UserSettings {
  // Account
  theme: string;
  language: string;
  
  // Signature
  signatures: EmailSignature[];
  includeSignatureInReply: boolean;
  includeSignatureInForward: boolean;
  signatureBeforeQuote: boolean;
  
  // Vacation/Out of Office
  enableVacationResponder: boolean;
  vacationMessage: string;
  vacationStartDate?: string;
  vacationEndDate?: string;
  
  // Forwarding
  enableForwarding: boolean;
  forwardingAddress: string;
  keepCopy: boolean;
  
  // Display
  emailsPerPage: number;
  defaultInboxView: string;
  showImages: string;
  enableThreading: boolean;
  previewPane: string;
  
  // Compose
  defaultFontFamily: string;
  defaultFontSize: number;
  defaultReplyBehavior: string;
  enableSpellCheck: boolean;
  sendOnCtrlEnter: boolean;
  
  // Notifications
  enableDesktopNotifications: boolean;
  enableEmailNotifications: boolean;
  enableSoundNotifications: boolean;
  notificationSound: string;
  
  // Keyboard shortcuts
  enableKeyboardShortcuts: boolean;
  
  // Filters
  filters: EmailFilter[];

  // Advanced Compose Settings
  defaultComposeFormat?: string;
  fullScreenCompose?: boolean;
  showToolbar?: boolean;
  undoSendSeconds?: number;
  includeOriginalInReply?: boolean;
  autoSaveInterval?: number;
  confirmBeforeSend?: boolean;
  warnEmptySubject?: boolean;
  warnNoAttachment?: boolean;
  smartCompose?: boolean;
  smartComposePersonalization?: boolean;
  smartReply?: boolean;
  grammarCheck?: boolean;
  styleCheck?: boolean;
  autoCompleteContacts?: boolean;
  maxAttachmentSize?: number;
  compressLargeAttachments?: boolean;
  useDriveForLargeFiles?: boolean;
  stripExifData?: boolean;
  virusScanAttachments?: boolean;
  imageResizeOption?: string;

  // Advanced Mail View Settings
  inboxType?: string;
  newestOnTop?: boolean;
  listDensity?: string;
  snippetLength?: number;
  showSenderAvatar?: boolean;
  highlightUnread?: boolean;
  showAttachmentIcon?: boolean;
  showLabelColors?: boolean;
  swipeRightAction?: string;
  swipeLeftAction?: string;
  enableHoverActions?: boolean;
  enableDynamicEmail?: boolean;
  showLinkPreviews?: boolean;
  openLinksInNewTab?: boolean;
  warnExternalLinks?: boolean;

  // Advanced Anti-Spam Settings
  spamFilterLevel?: string;
  spamProtection?: boolean;
  spamSensitivity?: number;
  spamAction?: string;
  checkSpf?: boolean;
  checkDkim?: boolean;
  checkDmarc?: boolean;
  blockExecutables?: boolean;
  autoDeleteSpam?: boolean;
  spamRetentionDays?: number;
  reportSpamToProvider?: boolean;
  spamLearning?: boolean;

  // Advanced Notification Settings
  notificationContent?: string;
  notificationActions?: boolean;
  groupNotifications?: boolean;
  showBadgeCount?: boolean;
  vipNotifications?: boolean;
  soundEnabled?: boolean;
  soundVolume?: number;
  quietHoursEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  quietHoursAllowVip?: boolean;
  quietHoursAllowUrgent?: boolean;
  folderNotifications?: string;

  // Advanced System Settings
  accentColor?: string;
  displayDensity?: string;
  timezone?: string;
  dateFormat?: string;
  timeFormat?: string;
  weekStartsOnMonday?: boolean;
  fontFamily?: string;
  fontSize?: number;
  lineSpacing?: number;
  highContrast?: boolean;
  reduceMotion?: boolean;
  largeClickTargets?: boolean;
  screenReaderOptimized?: boolean;
  focusIndicators?: boolean;
}

interface SettingsState {
  settings: UserSettings;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
  
  // Actions
  fetchSettings: () => Promise<void>;
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>;
  
  // Signature actions
  addSignature: (signature: Omit<EmailSignature, 'id'>) => void;
  updateSignature: (id: string, signature: Partial<EmailSignature>) => void;
  deleteSignature: (id: string) => void;
  setDefaultSignature: (id: string) => void;
  
  // Filter actions
  addFilter: (filter: Omit<EmailFilter, 'id'>) => void;
  updateFilter: (id: string, filter: Partial<EmailFilter>) => void;
  deleteFilter: (id: string) => void;
  toggleFilter: (id: string) => void;
}

const defaultSettings: UserSettings = {
  theme: 'light',
  language: 'en',
  
  signatures: [],
  includeSignatureInReply: true,
  includeSignatureInForward: true,
  signatureBeforeQuote: false,
  
  enableVacationResponder: false,
  vacationMessage: '',
  vacationStartDate: undefined,
  vacationEndDate: undefined,
  
  enableForwarding: false,
  forwardingAddress: '',
  keepCopy: true,
  
  emailsPerPage: 25,
  defaultInboxView: 'default',
  showImages: 'ask',
  enableThreading: true,
  previewPane: 'right',
  
  defaultFontFamily: 'Arial',
  defaultFontSize: 11,
  defaultComposeFormat: 'html',
  fullScreenCompose: false,
  showToolbar: true,
  undoSendSeconds: 10,
  includeOriginalInReply: true,
  autoSaveInterval: 60,
  confirmBeforeSend: false,
  warnEmptySubject: true,
  warnNoAttachment: true,
  defaultReplyBehavior: 'reply',
  enableSpellCheck: true,
  sendOnCtrlEnter: false,
  
  enableDesktopNotifications: true,
  enableEmailNotifications: false,
  enableSoundNotifications: true,
  notificationSound: 'default',
  notificationContent: 'full',
  notificationActions: true,
  groupNotifications: true,
  showBadgeCount: true,
  vipNotifications: false,
  
  enableKeyboardShortcuts: true,
  soundEnabled: true,
  soundVolume: 70,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
  quietHoursAllowVip: true,
  quietHoursAllowUrgent: false,
  folderNotifications: undefined,
  smartCompose: true,
  smartComposePersonalization: true,
  smartReply: true,
  grammarCheck: true,
  styleCheck: false,
  autoCompleteContacts: true,
  maxAttachmentSize: 25,
  compressLargeAttachments: true,
  useDriveForLargeFiles: true,
  stripExifData: true,
  virusScanAttachments: true,
  imageResizeOption: 'original',
  
  filters: [],

  // System Settings defaults
  accentColor: 'blue',
  displayDensity: 'comfortable',
  timezone: 'Asia/Kolkata',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  weekStartsOnMonday: false,
  fontFamily: 'Lato',
  fontSize: 16, // matches Zoho root font-size (16px) → body text renders at 14px
  lineSpacing: 1.3, // matches Zoho line spacing (18px/14px = 1.286)
  highContrast: false,
  reduceMotion: false,
  largeClickTargets: false,
  screenReaderOptimized: false,
  focusIndicators: true,
};

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: defaultSettings,
  isLoading: false,
  error: null,
  isSaving: false,

  fetchSettings: async () => {
    // Prevent multiple simultaneous fetches
    const state = get();
    if (state.isLoading) return;
    
    set({ isLoading: true, error: null });
    try {
      const response = await api.get('/users/settings');
      const data = response.data?.data || response.data;
      
      // Merge with defaults for any missing fields
      const mergedSettings = {
        ...defaultSettings,
        ...data,
        // fontSize (typography slider) comes from localStorage, NOT backend
        fontSize: localStorage.getItem('cruvz_typography_fontSize') 
          ? parseInt(localStorage.getItem('cruvz_typography_fontSize')!, 10)
          : defaultSettings.fontSize,
        // fontFamily (typography font) comes from localStorage, NOT backend
        fontFamily: localStorage.getItem('cruvz_typography_fontFamily')
          ? localStorage.getItem('cruvz_typography_fontFamily')!
          : defaultSettings.fontFamily,
        defaultFontFamily: data.defaultFontFamily || defaultSettings.defaultFontFamily,
        // defaultFontSize is protected from typography slider - only comes from backend
        defaultFontSize: data.defaultFontSize ?? defaultSettings.defaultFontSize,
        // Parse JSON fields if they're strings
        signatures: typeof data.signatures === 'string' 
          ? JSON.parse(data.signatures || '[]') 
          : (data.signatures || []),
        filters: typeof data.filters === 'string'
          ? JSON.parse(data.filters || '[]')
          : (data.filters || []),
      };
      
      set({ settings: mergedSettings, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to fetch settings',
        isLoading: false,
      });
    }
  },

  updateSettings: async (newSettings) => {
    set({ isSaving: true, error: null });
    try {
      const currentSettings = get().settings;
      const updatedSettings = { ...currentSettings, ...newSettings };
      
      // Build payload with ONLY the changed fields to prevent cross-page overwrites
      const payload: Record<string, any> = {};
      const changedKeys = Object.keys(newSettings) as (keyof UserSettings)[];
      
      for (const key of changedKeys) {
        const value = (updatedSettings as any)[key];
        
        // Handle special serialization cases
        if (key === 'signatures') {
          payload.signatures = JSON.stringify(value);
          payload.signature = (value as EmailSignature[]).find((s: EmailSignature) => s.isDefault)?.content || '';
        } else if (key === 'filters') {
          payload.filters = JSON.stringify(value);
        } else if (key === 'fontFamily') {
          // System Settings typography → NEVER sent to backend, only localStorage
          // Do NOT add to payload to prevent overwriting defaultFontFamily (compose font)
        } else if (key === 'fontSize') {
          // System Settings typography → NEVER sent to backend, only localStorage
          // Do NOT add to payload to prevent overwriting defaultFontSize
        } else if (key === 'defaultFontFamily') {
          // Compose Settings font → send directly, don't send fontFamily alias
          payload.defaultFontFamily = value;
        } else if (key === 'defaultFontSize') {
          // Compose Settings font size → send directly
          payload.defaultFontSize = value;
        } else {
          payload[key] = value;
        }
      }
      
      await api.put('/users/settings', payload);
      
      set({ settings: updatedSettings, isSaving: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to save settings',
        isSaving: false,
      });
      throw error;
    }
  },

  addSignature: (signature) => {
    const settings = get().settings;
    const newSignature: EmailSignature = {
      ...signature,
      id: Date.now().toString(),
      isDefault: settings.signatures.length === 0,
    };
    set({
      settings: {
        ...settings,
        signatures: [...settings.signatures, newSignature],
      },
    });
  },

  updateSignature: (id, updates) => {
    const settings = get().settings;
    set({
      settings: {
        ...settings,
        signatures: settings.signatures.map(s =>
          s.id === id ? { ...s, ...updates } : s
        ),
      },
    });
  },

  deleteSignature: (id) => {
    const settings = get().settings;
    set({
      settings: {
        ...settings,
        signatures: settings.signatures.filter(s => s.id !== id),
      },
    });
  },

  setDefaultSignature: (id) => {
    const settings = get().settings;
    set({
      settings: {
        ...settings,
        signatures: settings.signatures.map(s => ({
          ...s,
          isDefault: s.id === id,
        })),
      },
    });
  },

  addFilter: (filter) => {
    const settings = get().settings;
    const newFilter: EmailFilter = {
      ...filter,
      id: Date.now().toString(),
    };
    set({
      settings: {
        ...settings,
        filters: [...settings.filters, newFilter],
      },
    });
  },

  updateFilter: (id, updates) => {
    const settings = get().settings;
    set({
      settings: {
        ...settings,
        filters: settings.filters.map(f =>
          f.id === id ? { ...f, ...updates } : f
        ),
      },
    });
  },

  deleteFilter: (id) => {
    const settings = get().settings;
    set({
      settings: {
        ...settings,
        filters: settings.filters.filter(f => f.id !== id),
      },
    });
  },

  toggleFilter: (id) => {
    const settings = get().settings;
    set({
      settings: {
        ...settings,
        filters: settings.filters.map(f =>
          f.id === id ? { ...f, enabled: !f.enabled } : f
        ),
      },
    });
  },
}));
